import { Flex, Typography } from "antd"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useMatch } from "react-router-dom"

import { MAIN_CONTENT_STICKY_TOP_CLASS } from "../../../../components/main-content.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  selectActiveBranchId,
  selectCompanyIsMultiBranch
} from "../../../Branches/redux/branches.slice.ts"
import SettingsLayoutTabs from "./SettingsLayoutTabs.tsx"

const { Title, Text } = Typography

export function SettingsLayout() {
  const { t } = useTranslation()
  const moduleAccess = useAppSelector(
    (state) => state?.auth?.userInfo?.permissions?.codes
  )
  const isBranchWorkspace = useAppSelector(
    (state) =>
      selectCompanyIsMultiBranch(state) && !!selectActiveBranchId(state)
  )
  const isCompanyTab =
    !!useMatch(RoutesPaths.settings.company) &&
    !isBranchWorkspace &&
    !!moduleAccess?.settings?.company?.settings_company_view
  const rootRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)

  // A sticky header inside a tab pins below this block, so the block's height
  // is published for it; tabs wrap at narrow widths, so it is measured.
  useEffect(() => {
    const sticky = stickyRef.current
    const root = rootRef.current
    if (!sticky || !root || typeof ResizeObserver === "undefined") return
    const publish = () =>
      root.style.setProperty(
        "--settings-sticky-h",
        `${sticky.getBoundingClientRect().height}px`
      )
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(sticky)
    return () => observer.disconnect()
  }, [])

  return (
    // Grows with a tall tab so the sticky title never leaves its parent; a grid,
    // not `min-h-full` on the column, so the tabs' percentage heights resolve.
    <div ref={rootRef} className={"grid grid-cols-1 min-h-full -mt-2"}>
      <Flex vertical gap={"middle"}>
        <div
          ref={stickyRef}
          className={`${MAIN_CONTENT_STICKY_TOP_CLASS} bg-background z-10 pt-2`}
        >
          <Flex vertical className={isCompanyTab ? "mb-3" : undefined}>
            <Title
              className={isCompanyTab ? "text-start !mb-0" : "text-start"}
              level={2}
            >
              Settings
            </Title>
            {isCompanyTab && (
              <Text type="secondary">
                {t("settingsCompanyPage.layoutSubtitle")}
              </Text>
            )}
          </Flex>
          <SettingsLayoutTabs permissions={moduleAccess} />
        </div>
        <Flex flex={1}>
          <Outlet />
        </Flex>
      </Flex>
    </div>
  )
}
